import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Star, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { projectAPI, createQueryKey } from '../services/api';
import { getImageUrl } from '../config/runtime';
import toast from 'react-hot-toast';
import tagsData from '../data/tags.json';

// Persistent cover cache
const coverCache = new Map();

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const formatDuration = (minutes) => {
  if (!minutes) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? (mins > 0 ? `${hours}h ${mins}m` : `${hours}h`) : `${mins}m`;
};

const ProjectCard = ({
  project,
  showBookmark = false,
  savedStatus = { isSaved: false, savedInLists: [] },
  onBookmarkClick,
  onProjectClick,
  isMobile = false,
  navigateTo = 'starters',
  referrer = null,
  className = '',
  preferCapturedCover = false,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [coverUrl, setCoverUrl] = useState(() => coverCache.get(project?.id) || null);
  const [isCoverLoading, setIsCoverLoading] = useState(
    () => preferCapturedCover && navigateTo === 'projects' && project?.id && !coverCache.get(project.id)
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Cover image resolution for user projects
  useEffect(() => {
    if (!preferCapturedCover || navigateTo !== 'projects' || !project?.id) {
      setIsCoverLoading(false);
      return;
    }

    const cached = coverCache.get(project.id);
    if (cached) {
      setCoverUrl(cached);
      setIsCoverLoading(false);
      return;
    }

    let cancelled = false;
    setIsCoverLoading(true);

    (async () => {
      try {
        const res = await projectAPI.getImages(project.id, { limit: 10, sort: 'latest' });
        const imgs = res?.data?.data?.images || [];
        const userImage = imgs.find(img => 
          img?.url && ['user_upload', 'crop', 'system_generated', 'imported'].includes(img.source)
        );
        const anyImage = imgs.find(img => img?.url || typeof img === 'string');
        const url = userImage?.url || anyImage?.url || (typeof anyImage === 'string' ? anyImage : null);

        if (!cancelled && url) {
          coverCache.set(project.id, url);
          setCoverUrl(url);
        }
      } catch {
        // Silent fallback to template images
      } finally {
        if (!cancelled) setIsCoverLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [preferCapturedCover, navigateTo, project?.id]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => projectAPI.delete(project.id),
    onSuccess: () => {
      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      queryClient.invalidateQueries({ queryKey: createQueryKey.projects() });
      queryClient.invalidateQueries({ queryKey: ['community-finished-projects'] });
      queryClient.removeQueries({ queryKey: createQueryKey.project(project.id) });
      setShowDeleteConfirm(false);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete project');
      setShowDeleteConfirm(false);
    },
  });

  const handleClick = () => {
    if (showDeleteConfirm || deleteMutation.isPending) return;
    
    if (onProjectClick) {
      onProjectClick(project.id);
      return;
    }

    const path = navigateTo === 'projects' ? `/projects/${project.id}` : `/starters/${project.id}`;
    navigate(path, {
      state: { referrer, transitionDirection: 'forward', previousPath: window.location.pathname },
    });
  };

  const handleBookmark = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onBookmarkClick?.(e, project);
  };

  // Derive display values
  const imageUrl = coverUrl || project.images?.[0];
  const ownerAvatar = project.owner?.avatar ? getImageUrl(project.owner.avatar) : '/favicon.png';
  const ownerName = project.owner?.username || 'User';
  const timeAgo = formatTimeAgo(project.updatedAt);
  const tagInfo = project.tags?.[0] && tagsData.tags.find(t => t.id === project.tags[0]);
  const duration = formatDuration(project.estimatedDuration);
  const difficulty = project.difficulty ? project.difficulty.charAt(0).toUpperCase() + project.difficulty.slice(1) : null;
  const trailLength = project.trailLength?.value > 0 ? `${project.trailLength.value} ${project.trailLength.unit}` : null;

  return (
    <article
      className={`group cursor-pointer ${isMobile ? 'active:scale-[0.98] transition-transform' : ''} ${className}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      {/* Image Container - Airbnb-style nearly-square aspect ratio */}
      <div className="relative aspect-[20/19] bg-gray-100 dark:bg-gray-800 rounded-3xl overflow-hidden">
        {isCoverLoading ? (
          <div className="absolute inset-0 w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
        ) : imageUrl ? (
          <img
            src={getImageUrl(imageUrl)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              // Hide broken images
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-emerald-50 to-sky-50 dark:from-emerald-900/30 dark:to-sky-900/30 flex items-center justify-center">
            <span className="text-4xl opacity-40"></span>
          </div>
        )}

        {/* Category Badge - Top Left (always shows when available) */}
        {/* {tagInfo && (
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-[11px] font-medium rounded-full flex items-center gap-1">
            <span className="text-xs">{tagInfo.icon}</span>
            <span>{tagInfo.label}</span>
          </div>
        )} */}

        {/* Top Right: Status Badge OR Bookmark Button */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {/* Status Badge - LIVE */}
          {navigateTo === 'projects' && project.status === 'ongoing' && (
            <div className="px-2.5 py-1 bg-emerald-500 text-white text-[11px] font-semibold rounded-full flex items-center gap-1.5 shadow-lg">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              LIVE
            </div>
          )}

          {/* Status Badge - Complete */}
          {navigateTo === 'projects' && project.status === 'finished' && (
            <div className="px-2.5 py-1 bg-black/70 backdrop-blur-sm text-white text-[11px] font-medium rounded-full">
              Complete
            </div>
          )}

          {/* Bookmark Button */}
          {showBookmark && (
            <button
              onClick={handleBookmark}
              className="w-8 h-8 flex items-center justify-center rounded-full transition-transform hover:scale-110 active:scale-95"
              aria-label={savedStatus.isSaved ? 'Remove from saved' : 'Save'}
            >
              {savedStatus.isSaved ? (
                <HeartSolidIcon className="w-5 h-5 text-rose-500" />
              ) : (
                <HeartSolidIcon className="w-5 h-5 text-gray-700/70 dark:text-white/70" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="pt-3 space-y-1">
        {/* Title + Rating Row */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-xs sm:text-[15px] text-gray-900 dark:text-white leading-snug line-clamp-1">
            {project.name}
          </h3>
          {project.rating?.average > 0 && (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Star className="w-3.5 h-3.5 fill-current text-gray-900 dark:text-white" />
              <span className="text-xs text-gray-900 dark:text-white">{project.rating.average.toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Location */}
        {project.formattedLocation && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
            {project.formattedLocation}
          </p>
        )}

        {/* Metadata Row */}
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          {/* Owner */}
          <div className="flex items-center gap-1.5 min-w-0">
            <img
              src={ownerAvatar}
              alt=""
              className="w-5 h-5 rounded-full object-cover flex-shrink-0"
            />
            <span className="truncate">{ownerName}</span>
            {project.isMultiplayer && project.participantCount > 0 && (
              <span className="text-gray-400">+{project.participantCount}</span>
            )}
          </div>

          {timeAgo && (
            <>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="flex-shrink-0">{timeAgo}</span>
            </>
          )}
        </div>

        {/* Duration/Difficulty/Trail - Tertiary info */}
        {(duration || difficulty || trailLength) && (
          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
            {difficulty && <span>{difficulty}</span>}
            {difficulty && (duration || trailLength) && <span>·</span>}
            {trailLength && <span>{trailLength}</span>}
            {trailLength && duration && <span>·</span>}
            {duration && <span>Est. {duration}</span>}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowDeleteConfirm(false)}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Delete project?</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  "{project.name}" will be permanently deleted.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors flex items-center gap-2"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default ProjectCard;
